/// <amd-module name="Widgets/Burndown/BurndownWidgetComponents/BurndownSettingsManager" />
import { BacklogsQuery } from "Analytics/Boards/BoardsQueries/BacklogsQuery";
import { ICacheableQueryService } from "Dashboards/WidgetCommon/DashboardsCacheableQueryService";
import { onWidgetFailure } from "Dashboards/WidgetCommon/WidgetTelemetry";
import { WidgetConfigurationProps, WidgetProps, WidgetTeamContext } from "Dashboards/WidgetContracts/WidgetProps";
import * as TfsWidgetContext from "Tfs/Widget/Context";
import { IVssPageContext } from "VSS/Platform/Context";
import { addDays, DateSampleInterval, DayOfWeek, toLocalDateIsoString } from "WidgetCommon/DateUtils/DateUtils";
import { AggregationMode } from "WidgetCommon/ModernWidgetTypes/CommonConfigurationTypes";
import { SettingsManagerBase } from "WidgetCommon/ModernWidgetTypes/SettingsManagerBase";
import { AsyncDataHelpers } from "./AsyncDataHelpers";
import { BurndownConstants } from "./BurndownConstants";
import { WorkItemTypeFilterMode } from "./BurndownDataContract";
import { BurndownSettings, DateSampleMode } from "./BurndownSettings";

export default class BurndownSettingsManager extends SettingsManagerBase<BurndownSettings> {
    private static async packDefaultSettings(pageContext: IVssPageContext, teamContext: WidgetTeamContext): Promise<BurndownSettings> {
        const endDate = new Date();
        const startDate = addDays(endDate, -14);
        const teamId = teamContext.id;
        const projectId = TfsWidgetContext.getProject(pageContext).id;

        const queryCache = pageContext.getService<ICacheableQueryService>("IDashboardsCacheableQueryService");
        const backlogs = await queryCache.getCacheableQueryResult(new BacklogsQuery(projectId, [teamId]));
        // Take the first visible matching backlog, which is comes from the highest
        const defaultBacklogIndex = backlogs.findIndex(backlog => {
            return backlog.IsBoardVisible;
        });
        const defaultBacklogRefName = (defaultBacklogIndex >= 0 && backlogs[defaultBacklogIndex].BoardCategoryReferenceName) || "";

        const defaultSettings: BurndownSettings = {
            /** Spec required initial default values */
            includeBugsForRequirementCategory: false,
            burndownTrendlineEnabled: true,
            totalScopeTrendlineEnabled: true,
            completedWorkEnabled: false,
            stackByWorkItemTypeEnabled: false,
            showResolvedItemsAsCompletedEnabled: false,
            aggregation: {
                identifier: AggregationMode.Count,
                settings: ""
            },
            workItemTypeFilter: {
                identifier: WorkItemTypeFilterMode[WorkItemTypeFilterMode.BacklogCategory],
                settings: defaultBacklogRefName
            },
            timePeriodConfiguration: {
                startDate: toLocalDateIsoString(startDate),
                samplingConfiguration: {
                    identifier: DateSampleMode.ByDateInterval,
                    settings: {
                        endDate: toLocalDateIsoString(endDate),
                        lastDayOfWeek: DayOfWeek.Friday,
                        sampleInterval: DateSampleInterval.Days
                    }
                }
            },
            teams: [
                {
                    teamId: teamContext.id,
                    projectId: TfsWidgetContext.getProject(pageContext).id
                }
            ],
            fieldFilters: []
        };

        return defaultSettings;
    }

    public constructor(protected pageContext: IVssPageContext, protected teamContext: WidgetTeamContext) {
        super();
    }

    public getFeatureName(): string {
        return BurndownConstants.featureName;
    }

    /**
     * Performs neccessary legwork to produce default Burndown Settings, if possible.
     */
    public generateDefaultSettings(): Promise<BurndownSettings> {
        return Promise.resolve(BurndownSettingsManager.packDefaultSettings(this.pageContext, this.teamContext));
    }

    /**
     * Verifies widget is properly configured for viewing (This means it has configuration, and  that configuration is accepted for rendering).
     */
    public async hasSatisfactoryConfiguration(widgetProps: WidgetConfigurationProps): Promise<boolean> {
        let isConfigured = this.isConfigured(widgetProps);
        if (isConfigured) {
            const burndownSettings = JSON.parse(widgetProps.settings || "") as BurndownSettings;

            // Reject configuration if configured with bugs for Reqs & Project doesn't employ a known Bug Category
            // Config UI will highlight the issue.
            isConfigured =
                burndownSettings.includeBugsForRequirementCategory != true ||
                (await AsyncDataHelpers.getLegacyProjectData(this.pageContext)).ProjectUsesKnownBugCategoryName;

            // Log for occurrences of failures caused by bugs for Requirement Category
            if (!isConfigured) {
                onWidgetFailure(
                    this.pageContext,
                    widgetProps as WidgetProps,
                    "Widget Rendering blocked - usage of include bugs option was blocked on customized Xml Process, until widget is reconfigured.",
                    "BurndownSettingsManager.hasSatisfactoryConfiguration()",
                    {
                        ErrorDetail:
                            "Widget Rendering blocked - usage of include bugs option was blocked on customized Xml Process, until widget is reconfigured."
                    }
                );
            }
        }
        return isConfigured;
    }
}
